import { useMemo, useRef } from 'react';
import type { ChangeEvent } from 'react';
import { useTheme } from '@course-authoring/shared-types';
import type { ImageLibraryItem } from '../api/imageLibraryApi';
import { ImageGrid } from './ImageGrid';
import type { LibraryPanelState } from './libraryPanelTypes';
import { useLibraryPanelStyles } from './libraryPanelStyles';
import type { BrowseSubTab } from './imageInsertTypes';

interface LibraryBrowseViewProps {
  state: LibraryPanelState;
  onSearchChange: (event: ChangeEvent<HTMLInputElement>) => void;
  onImageSelect: (image: ImageLibraryItem) => void;
  onImageDoubleClick: (image: ImageLibraryItem) => void;
  onSubTabChange: (tab: BrowseSubTab) => void;
  onUseImage: () => void;
  onLoadMore: () => Promise<boolean | undefined>;
  onRetry: () => void;
}

export function LibraryBrowseView({
  state,
  onSearchChange,
  onImageSelect,
  onImageDoubleClick,
  onSubTabChange,
  onUseImage,
  onLoadMore,
  onRetry,
}: LibraryBrowseViewProps) {
  const libraryStyles = useLibraryPanelStyles();
  const { tokens } = useTheme();
  const styles = useMemo(() => createBrowseStyles(tokens), [tokens]);
  const countRef = useRef<HTMLParagraphElement>(null);
  const moreRef = useRef<HTMLButtonElement>(null);
  const scopeKey = `${state.accountId}:${state.browseSubTab}:${state.searchQuery}`;
  const scopeRef = useRef(scopeKey);
  scopeRef.current = scopeKey;
  const loadMore = async () => {
    const button = moreRef.current;
    const hadFocus = document.activeElement === button;
    const complete = await onLoadMore();
    if (complete && hadFocus && scopeRef.current === scopeKey
      && (document.activeElement === button || document.activeElement === document.body)) countRef.current?.focus({ preventScroll: true });
  };
  const hasSearchQuery = state.searchQuery.trim().length > 0;
  const accountLibraryLabel = state.accountName
    ? `${state.accountName} Image Library`
    : 'Account Image Library';

  return (
    <div style={libraryStyles.content} role="region" aria-label="Image library browser">
      <div style={styles.subTabBar} role="tablist" aria-label="Library sections">
        <button
          type="button"
          role="tab"
          aria-selected={state.browseSubTab === 'emtrain'}
          style={{
            ...styles.subTab,
            ...(state.browseSubTab === 'emtrain' ? styles.subTabActive : {}),
          }}
          onClick={() => onSubTabChange('emtrain')}
        >
          Emtrain Image Library
        </button>
        {state.accountId !== null && (
          <button
            type="button"
            role="tab"
            aria-selected={state.browseSubTab === 'saved'}
            style={{
              ...styles.subTab,
              ...(state.browseSubTab === 'saved' ? styles.subTabActive : {}),
            }}
            onClick={() => onSubTabChange('saved')}
          >
            {accountLibraryLabel}
          </button>
        )}
      </div>

      <div style={libraryStyles.toolbar} role="search">
        <div style={libraryStyles.searchContainer}>
          <input
            type="text"
            value={state.searchQuery}
            onChange={onSearchChange}
            placeholder="Search images..."
            style={libraryStyles.searchInput}
            aria-label="Search images"
            disabled={state.isUploading}
          />
        </div>
      </div>

      {state.error && (
        <div style={libraryStyles.errorMessage} role="alert" aria-live="polite">
          {state.error}
          <button type="button" style={styles.subTab} onClick={onRetry}>Retry</button>
        </div>
      )}

      {state.uploadError && (
        <div style={libraryStyles.uploadErrorContainer} role="alert" aria-live="assertive">
          <div style={libraryStyles.uploadErrorMessage}>
            <span style={libraryStyles.uploadErrorIcon} aria-hidden="true">!</span>
            <span>{state.uploadError}</span>
          </div>
        </div>
      )}

      <div style={libraryStyles.gridContainer}>
        {!state.isLoading && state.images.length === 0 && !state.error && (
          <div style={libraryStyles.noResults} role="status" aria-live="polite">
            <p style={libraryStyles.noResultsText}>
              {hasSearchQuery ? 'No matching images' : 'No images saved to this library yet.'}
            </p>
            {hasSearchQuery && (
              <p style={libraryStyles.noResultsHint}>Try a different search term</p>
            )}
          </div>
        )}

        <ImageGrid
          images={state.images}
          selectedId={state.selectedImage?.id ?? null}
          onSelect={onImageSelect}
          onDoubleClick={onImageDoubleClick}
          isLoading={state.isLoading}
          isInitialLoad={state.isInitialLoad}
        />
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px', padding: '24px 0' }}>
          <p ref={countRef} tabIndex={-1} role="status" style={{ margin: 0, color: tokens.text.muted, fontSize: '14px' }}>
            {state.isLoading ? 'Loading...' : state.error ? 'Count unavailable'
              : state.images.length >= state.totalCount && state.totalCount > 0
                ? `All ${state.totalCount} ${hasSearchQuery ? 'matching images' : 'images'} shown`
                : `Showing ${state.images.length} of ${state.totalCount} ${hasSearchQuery ? 'matching images' : 'images'}`}
          </p>
          {state.isLoadingMore && <span role="status">Loading more images...</span>}
          {state.appendError && <p role="alert">Could not load more images. Try again.</p>}
          {!state.error && state.images.length < state.totalCount && (
            <button ref={moreRef} type="button" style={styles.subTab} disabled={state.isLoading || state.isLoadingMore}
              onClick={() => void loadMore()}>
              {state.isLoadingMore ? 'Loading more images...' : state.appendError ? 'Retry loading more images' : 'Load more images'}
            </button>
          )}
        </div>
      </div>

      {state.selectedImage && (
        <div style={styles.useImageBar}>
          <button
            type="button"
            style={styles.useImageButton}
            onClick={onUseImage}
            disabled={state.isLoading || Boolean(state.error)}
            aria-label="Use selected image"
          >
            Use Image
          </button>
        </div>
      )}
    </div>
  );
}

function createBrowseStyles(
  tokens: ReturnType<typeof useTheme>['tokens']
): Record<string, React.CSSProperties> {
  return {
    subTabBar: {
      display: 'flex',
      gap: '4px',
      marginBottom: '12px',
      flexShrink: 0,
    },
    subTab: {
      padding: '6px 14px',
      fontSize: '13px',
      fontWeight: 500,
      color: tokens.text.muted,
      background: 'none',
      border: `1px solid ${tokens.text.subtle}`,
      borderRadius: '20px',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      transition: `background-color ${tokens.ui.transitionDefault}, color ${tokens.ui.transitionDefault}`,
    },
    subTabActive: {
      color: tokens.text.onBrand,
      backgroundColor: tokens.text.primary,
      borderColor: tokens.text.primary,
    },
    useImageBar: {
      display: 'flex',
      justifyContent: 'flex-end',
      padding: '8px 0 0 0',
      flexShrink: 0,
    },
    useImageButton: {
      padding: '8px 16px',
      fontSize: '14px',
      fontWeight: 500,
      border: 'none',
      borderRadius: '6px',
      backgroundColor: tokens.ui.selectionBorder,
      color: tokens.text.onBrand,
      cursor: 'pointer',
      minWidth: '44px',
      minHeight: '44px',
    },
  };
}
